import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import type { RecoverStepAttribution } from "@redotech/redo-billing-common/recover/recover-attribution-types";
import { DiscountSettings } from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { formatDiscountDescription } from "@redotech/redo-discounts-common/discount/format-discount-description";
import { RecoverStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import DiscountIcon from "@redotech/redo-icons/percent-02_filled.svg";
import { RecoverConfig } from "@redotech/redo-marketing-templates-common/recover-config";
import { useIsRedoAdmin } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantDiscountsRpcClientContext } from "@redotech/redo-merchant-app-common/discounts-rpc-client-provider";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Treatment } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PartialWithRequiredType, StepTypeDetailsProps } from ".";
import { AutomationAbTestEditor } from "./automation-ab-test-editor";
import { AutomationAbTestInspector } from "./automation-ab-test-inspector";
import { AutomationAbTestResultsPanel } from "./automation-ab-test-results-panel";
import { useAbTestEditor } from "./common/use-ab-test-editor";

type Props = StepTypeDetailsProps<PartialWithRequiredType<RecoverStep>>;

export function RecoverConfigPreview({
  step,
  setStep,
  save,
  automationId,
  schemaType,
}: Props) {
  const { t } = useTranslation();
  const stepId = step.id;
  const recoverConfigId = step.templateId;
  const navigate = useNavigate();
  const location = useLocation();

  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const discountsRpcClient = useRequiredContext(
    RedoMerchantDiscountsRpcClientContext,
  );

  const [recoverConfigName, setRecoverConfigName] = useState("");
  const [creating, setCreating] = useState(false);

  const recoverConfigLoad = useLoad(async () => {
    if (!recoverConfigId) {
      return null;
    }
    try {
      return await marketingRpcClient.getRecoverConfigById({
        id: recoverConfigId,
      });
    } catch {
      return null;
    }
  }, [recoverConfigId, marketingRpcClient]);

  const recoverConfig = recoverConfigLoad.value;

  const [discountLoad, triggerDiscountLoad] = useTriggerLoad(async () => {
    const discountId = recoverConfig?.discountId;
    if (!discountId) {
      return null;
    }
    try {
      return await discountsRpcClient.getDiscount({ _id: discountId });
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (recoverConfig?.discountId) {
      triggerDiscountLoad();
    }
  }, [recoverConfig?.discountId, triggerDiscountLoad]);

  const discount = discountLoad.value;
  const discountLoading = recoverConfig?.discountId && discountLoad.pending;

  const handleCreateRecoverStep = useCallback(async () => {
    if (!recoverConfigName) {
      return;
    }
    setCreating(true);
    try {
      const newConfig = await marketingRpcClient.createRecoverConfig({
        name: recoverConfigName,
        schemaType,
      });
      setStep({ ...step, templateId: newConfig._id });
      save?.();
      navigate(
        `../../recover/builder/${newConfig._id}?returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}`,
      );
    } catch (error) {
      if (error instanceof Error) {
        toast(
          t("Failed to create recover step: {{message}}", {
            message: error.message,
          }),
          { variant: "error" },
        );
      } else {
        toast(t("Failed to create recover step"), { variant: "error" });
      }
    } finally {
      setCreating(false);
    }
  }, [
    recoverConfigName,
    setStep,
    step,
    save,
    marketingRpcClient,
    navigate,
    location.pathname,
    stepId,
    schemaType,
    t,
  ]);

  const createRecoverVariantTemplate = useCallback(
    async (name: string) => {
      const newConfig = await marketingRpcClient.createRecoverConfig({
        name,
        schemaType,
      });
      return newConfig._id;
    },
    [marketingRpcClient, schemaType],
  );

  const abTest = useAbTestEditor({
    automationId,
    stepId,
    stepType: "recover",
    splitId: step.splitId,
    existingTemplateId: recoverConfig?._id,
    existingTemplateName: recoverConfig?.name,
    onSplitIdChange: (splitId) => setStep({ ...step, splitId }),
    onSave: save,
    createTemplate: createRecoverVariantTemplate,
    getEditPath: (recoverConfigId, returnPath) =>
      `../../recover/builder/${recoverConfigId}?${returnPath}&stepType=${step.type}`,
    getViewPath: (recoverConfigId, returnPath) =>
      `../../recover/builder/${recoverConfigId}?${returnPath}&stepType=${step.type}&readonly=true`,
  });

  const buildReturnPath = useCallback(
    () =>
      `returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}&openAbTestEditor=true`,
    [location.pathname, stepId],
  );

  const handleEditVariant = useCallback(
    (treatment: Treatment) => {
      if (!treatment.templateId) {
        return;
      }
      save?.();
      navigate(
        `../../recover/builder/${treatment.templateId}?${buildReturnPath()}&stepType=${step.type}`,
      );
    },
    [navigate, buildReturnPath, save, step.type],
  );

  const handleViewVariant = useCallback(
    (treatment: Treatment) => {
      if (!treatment.templateId) {
        return;
      }
      navigate(
        `../../recover/builder/${treatment.templateId}?${buildReturnPath()}&stepType=${step.type}&readonly=true`,
      );
    },
    [navigate, buildReturnPath, step.type],
  );

  const handleOpenVariantModal = useCallback(() => {
    const variantLetter = String.fromCharCode(
      65 + (abTest.splitWithTreatments?.treatments.length ?? 1),
    );
    const variantName = `Variant ${variantLetter}`;
    abTest.variantModal.open(
      variantName,
      t("{{variantName}} {{productName}} Config", {
        variantName,
        productName: "Recover",
      }),
    );
  }, [abTest.splitWithTreatments?.treatments.length, abTest.variantModal, t]);

  const handleEdit = () => {
    if (!recoverConfig) {
      return;
    }
    navigate(
      `../../recover/builder/${recoverConfig._id}?returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}`,
    );
  };

  const handleSelectWinner = (winningTemplateId: string) => {
    setStep({ ...step, templateId: winningTemplateId, splitId: undefined });
    save?.();
    abTest.refetch();
  };

  if (abTest.view === "loading" || recoverConfig === undefined) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (abTest.view === "results" && automationId) {
    return (
      <AutomationAbTestResultsPanel
        automationId={automationId}
        automationStepId={stepId}
        onSelectWinner={handleSelectWinner}
        onViewVariant={handleViewVariant}
        stepType="recover"
        title={t("{{productName}} A/B test", { productName: "Recover" })}
      />
    );
  }

  if (abTest.view === "editor" && abTest.splitWithTreatments) {
    return (
      <>
        <AutomationAbTestEditor
          onAddVariant={handleOpenVariantModal}
          onClose={abTest.closeEditor}
          onEditVariant={handleEditVariant}
          renderVariantPreview={(treatment) => (
            <RecoverVariantPreview templateId={treatment.templateId} />
          )}
          splitWithTreatments={abTest.splitWithTreatments}
          stepType="recover"
          title={t("{{productName}} A/B test", { productName: "Recover" })}
        />

        <RedoModal
          isOpen={abTest.variantModal.isOpen}
          modalSize="md"
          onModalCloseRequested={abTest.variantModal.close}
          primaryButton={{
            text: t("Continue"),
            onClick: abTest.variantModal.create,
            disabled: !abTest.variantModal.templateName,
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: abTest.variantModal.close,
          }}
          title={t("Create recover config for {{variantName}}", {
            variantName: abTest.variantModal.variantName,
          })}
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" textColor="secondary">
              {t("Configuration name")}
            </Text>
            <RedoTextInput
              placeholder={t("Enter configuration name")}
              setValue={abTest.variantModal.setTemplateName}
              size="sm"
              value={abTest.variantModal.templateName}
            />
          </Flex>
        </RedoModal>
      </>
    );
  }

  return (
    <InspectorLayout title="Recover">
      <InspectorSection label={t("Messages")}>
        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" textColor="secondary">
              {t("Configuration")}
            </Text>
            {recoverConfig && (
              <RedoButton
                hierarchy="secondary"
                IconLeading={EditPencilIcon}
                onClick={handleEdit}
                size="xs"
                text={t("Edit")}
              />
            )}
          </Flex>

          {recoverConfig ? (
            <RecoverConfigCard
              config={recoverConfig}
              discount={discount}
              discountLoading={!!discountLoading}
            />
          ) : (
            <Flex dir="column" gap="sm" w="full">
              <RedoTextInput
                placeholder={t("Enter recover step name")}
                setValue={setRecoverConfigName}
                size="sm"
                value={recoverConfigName}
              />
              <RedoButton
                disabled={!recoverConfigName || creating}
                hierarchy="secondary"
                onClick={handleCreateRecoverStep}
                pending={creating}
                size="sm"
                text={t("Configure recover step")}
              />
            </Flex>
          )}
        </Flex>
      </InspectorSection>

      <InspectorSection label={t("Step")}>
        <InspectorField label={null}>
          <RedoToggle
            label={t("Deactivate")}
            setValue={(disabled) => setStep({ ...step, disabled })}
            size="sm"
            value={step.disabled ?? false}
          />
        </InspectorField>
      </InspectorSection>

      <AttributionSection setStep={setStep} step={step} />

      {automationId && (
        <AutomationAbTestInspector
          existingTest={abTest.splitWithTreatments}
          hasTemplate={!!recoverConfig?._id}
          onOpenEditor={abTest.openEditor}
        />
      )}
    </InspectorLayout>
  );
}

const RecoverConfigCard = memo(function RecoverConfigCard({
  config,
  discount,
  discountLoading,
}: {
  config: RecoverConfig;
  discount:
    | { name: string; discountSettings: DiscountSettings }
    | null
    | undefined;
  discountLoading: boolean;
}) {
  const { t } = useTranslation();
  const outreachTemplate = config.outreachTemplate;

  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      pb="md"
      w="full"
    >
      <Flex align="center" justify="space-between" pt="sm" px="md">
        <Text fontSize="sm" fontWeight="semibold">
          {config.name}
        </Text>
      </Flex>

      {(discountLoading || discount) && (
        <>
          <Divider />
          {discountLoading ? (
            <Flex align="center" justify="center" px="md" py="xs">
              <RedoSpinner size="xs" />
            </Flex>
          ) : discount ? (
            <Flex align="center" justify="space-between" px="md" py="xs">
              <Flex align="center" gap="xs">
                <DiscountIcon height={14} width={14} />
                <Text fontSize="xs" textColor="secondary">
                  {discount.name}
                </Text>
              </Flex>
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {formatDiscountDescription(discount)}
              </Text>
            </Flex>
          ) : null}
        </>
      )}

      {outreachTemplate != null && (
        <>
          <Divider />
          <Flex px="md" py="xs">
            <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
              {t("MESSAGE")}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between" px="md" py="xs">
            <Text fontSize="xs" textColor="secondary">
              {t("Outreach")}
            </Text>
            <Text
              fontSize="xs"
              overflow="hidden"
              style={{ maxWidth: "120px" }}
              textColor="secondary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {config.agentOutreachEnabled ? "Commerce Agent" : t("Template")}
            </Text>
          </Flex>
        </>
      )}
    </Flex>
  );
});

const RecoverVariantPreview = memo(function RecoverVariantPreview({
  templateId,
}: {
  templateId: string | undefined;
}) {
  const { t } = useTranslation();
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const discountsRpcClient = useRequiredContext(
    RedoMerchantDiscountsRpcClientContext,
  );

  const configLoad = useLoad(async () => {
    if (!templateId) {
      return null;
    }
    try {
      return await marketingRpcClient.getRecoverConfigById({ id: templateId });
    } catch {
      return null;
    }
  }, [templateId, marketingRpcClient]);

  const config = configLoad.value;

  const discountLoad = useLoad(async () => {
    if (!config?.discountId) {
      return null;
    }
    try {
      return await discountsRpcClient.getDiscount({ _id: config.discountId });
    } catch {
      return null;
    }
  }, [config?.discountId, discountsRpcClient]);

  if (!templateId) {
    return (
      <Text fontSize="xs" textColor="secondary">
        {t("No template selected")}
      </Text>
    );
  }

  if (config === undefined) {
    return (
      <Flex align="center" justify="center" p="sm">
        <RedoSpinner size="xs" />
      </Flex>
    );
  }

  if (!config) {
    return (
      <Text fontSize="xs" textColor="secondary">
        {t("No recover configuration found")}
      </Text>
    );
  }

  return (
    <RecoverConfigCard
      config={config}
      discount={discountLoad.value}
      discountLoading={!!config.discountId && discountLoad.pending}
    />
  );
});

const AttributionSection = memo(function AttributionSection({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RecoverStep>;
  setStep: (step: PartialWithRequiredType<RecoverStep>) => void;
}) {
  const { t } = useTranslation();
  const isRedoAdmin = useIsRedoAdmin();
  const team = useRequiredContext(TeamContext);
  const teamAttribution = team.settings.recover?.attribution;
  const defaultAttribution: RecoverStepAttribution = {
    discountSettings: {
      enabled: teamAttribution?.discountSettings.enabled ?? false,
    },
    clickSettings: { enabled: teamAttribution?.clickSettings.enabled ?? false },
    meaningfulConversationSettings: {
      enabled: teamAttribution?.meaningfulConversationSettings.enabled ?? false,
    },
  };
  const hasOverride = step.attribution != null;
  const attribution: RecoverStepAttribution = hasOverride
    ? {
        discountSettings: {
          enabled:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            step.attribution!.discountSettings?.enabled ??
            defaultAttribution.discountSettings.enabled,
        },
        clickSettings: {
          enabled:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            step.attribution!.clickSettings?.enabled ??
            defaultAttribution.clickSettings.enabled,
        },
        meaningfulConversationSettings: {
          enabled:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            step.attribution!.meaningfulConversationSettings?.enabled ??
            defaultAttribution.meaningfulConversationSettings.enabled,
        },
      }
    : defaultAttribution;

  const updateAttribution = (updates: Partial<RecoverStepAttribution>) => {
    setStep({ ...step, attribution: { ...attribution, ...updates } });
  };

  const attributionSummary = (() => {
    const discount = attribution.discountSettings.enabled;
    const click = attribution.clickSettings.enabled;
    const conversation = attribution.meaningfulConversationSettings.enabled;
    if (discount && click && conversation) {
      return t("Attributing via discount, click, and meaningful conversation");
    }
    if (discount && click) {
      return t("Attributing via discount and click");
    }
    if (discount && conversation) {
      return t("Attributing via discount and meaningful conversation");
    }
    if (click && conversation) {
      return t("Attributing via click and meaningful conversation");
    }
    if (discount) {
      return t("Attributing via discount");
    }
    if (click) {
      return t("Attributing via click");
    }
    if (conversation) {
      return t("Attributing via meaningful conversation");
    }
    return t("No attribution methods enabled");
  })();

  if (!isRedoAdmin) {
    return (
      <InspectorSection label={t("Attribution")}>
        <Text fontSize="xs" textColor="secondary">
          {hasOverride ? attributionSummary : t("Using account defaults")}
        </Text>
      </InspectorSection>
    );
  }

  return (
    <InspectorSection label={t("Attribution")}>
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="sm">
          <InspectorField label={null}>
            <RedoToggle
              disabled={!hasOverride}
              label={t("Discount attribution")}
              setValue={(enabled) =>
                updateAttribution({ discountSettings: { enabled } })
              }
              size="sm"
              value={attribution.discountSettings.enabled}
            />
          </InspectorField>
          <InspectorField label={null}>
            <RedoToggle
              disabled={!hasOverride}
              label={t("Click attribution")}
              setValue={(enabled) =>
                updateAttribution({
                  clickSettings: { ...attribution.clickSettings, enabled },
                })
              }
              size="sm"
              value={attribution.clickSettings.enabled}
            />
          </InspectorField>
          <InspectorField label={null}>
            <RedoToggle
              disabled={!hasOverride}
              label={t("Meaningful conversation")}
              setValue={(enabled) =>
                updateAttribution({
                  meaningfulConversationSettings: {
                    ...attribution.meaningfulConversationSettings,
                    enabled,
                  },
                })
              }
              size="sm"
              value={attribution.meaningfulConversationSettings.enabled}
            />
          </InspectorField>
          <Flex pt="md">
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                if (hasOverride) {
                  const { attribution: _, ...rest } = step;
                  setStep(rest as PartialWithRequiredType<RecoverStep>);
                } else {
                  setStep({ ...step, attribution: defaultAttribution });
                }
              }}
              size="xs"
              text={
                hasOverride ? t("Reset to account defaults") : t("Override")
              }
            />
          </Flex>
        </Flex>
      </Flex>
    </InspectorSection>
  );
});
